import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaHeadset, FaTimes, FaUserClock } from 'react-icons/fa';
import adminService from '../../services/adminService';
import { listSupportChats } from '../../services/supportChatService';
import {
  playNotifyBeep,
  playQueueBeep,
  unlockNotifyBeep,
} from '../../utils/notifyBeep';
import './SupportChatToast.css';

const TOAST_TTL_MS = 12000;
const QUEUE_TOAST_TTL_MS = 16000;
const POLL_MS = 6000;
const MAX_TOASTS = 4;
const ADMIN_TAB_KEY = 'nexusAdminTab';

/**
 * Notificador global do Chat VIP — funciona em qualquer página do site
 * enquanto houver sessão admin com permissão de support_chat.
 */
const SupportChatNotifier = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [toasts, setToasts] = useState([]);
  const unreadMapRef = useRef(new Map());
  const queuedIdsRef = useRef(new Set());
  const staffIdRef = useRef('');
  const readyRef = useRef(false);
  const timersRef = useRef(new Map());
  const authFailRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const pushToast = useCallback(
    ({ chat, kind }) => {
      const id = `${kind}-${chat.id}-${Date.now()}`;
      const isQueue = kind === 'queue';
      const toast = {
        id,
        kind,
        chatId: chat.id,
        name: chat.globalName || chat.username || 'Jogador',
        preview: isQueue
          ? chat.lastMessagePreview || 'Aguardando atendimento na fila'
          : chat.lastMessagePreview || 'Nova mensagem no Chat VIP',
        topicLabel: chat.topicLabel || '',
      };

      setToasts((prev) => [toast, ...prev].slice(0, MAX_TOASTS));
      if (isQueue) playQueueBeep();
      else playNotifyBeep();

      const timer = setTimeout(
        () => dismiss(id),
        isQueue ? QUEUE_TOAST_TTL_MS : TOAST_TTL_MS
      );
      timersRef.current.set(id, timer);
    },
    [dismiss]
  );

  const isAwayFromDesk = useCallback(() => {
    const path = location.pathname || '';
    if (path !== '/admin') return true;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return true;
    }
    try {
      return sessionStorage.getItem(ADMIN_TAB_KEY) !== 'support_chat';
    } catch {
      return true;
    }
  }, [location.pathname]);

  const resetWatchState = useCallback(() => {
    readyRef.current = false;
    unreadMapRef.current = new Map();
    queuedIdsRef.current = new Set();
    staffIdRef.current = '';
  }, []);

  const refreshEnabled = useCallback(async () => {
    const token = adminService.getAccessToken();
    if (!token) {
      setEnabled(false);
      resetWatchState();
      return;
    }

    try {
      const session = await adminService.fetchSession();
      const owner = Boolean(session.isOwner);
      const tabs = Array.isArray(session.tabs) ? session.tabs : [];
      const ok = owner || tabs.includes('support_chat');
      staffIdRef.current = String(session?.user?.id || '');
      setEnabled(ok);
      if (!ok) resetWatchState();
      authFailRef.current = 0;
    } catch (err) {
      authFailRef.current += 1;
      if (authFailRef.current >= 2 || err?.code === 'UNAUTHORIZED' || err?.code === 'NO_TOKEN') {
        setEnabled(false);
        resetWatchState();
      }
    }
  }, [resetWatchState]);

  const poll = useCallback(async () => {
    if (!adminService.getAccessToken()) {
      setEnabled(false);
      return;
    }
    if (!enabled) return;

    try {
      const data = await listSupportChats({ filter: 'open', limit: 40 });
      const chats = data.chats || [];
      authFailRef.current = 0;

      const myId = String(staffIdRef.current || '');
      const queued = chats.filter((c) => c.status === 'queued');
      const nextQueued = new Set(queued.map((c) => c.id));
      const mine = myId
        ? chats.filter((c) => String(c.assigneeId || '') === myId)
        : [];

      if (readyRef.current) {
        // Fila: todos os staff veem só entrada nova na fila
        for (const c of queued) {
          if (!queuedIdsRef.current.has(c.id)) {
            pushToast({ chat: c, kind: 'queue' });
          }
        }

        // Mensagem: só nos chats que EU assumi
        if (isAwayFromDesk() && myId) {
          for (const c of mine) {
            const prev = unreadMapRef.current.get(c.id);
            const next = Number(c.unreadStaff) || 0;
            // Primeira vez que o chat aparece como "meu" = baseline, sem popup
            if (prev === undefined) continue;
            if (next > prev) pushToast({ chat: c, kind: 'message' });
          }
        }
      }

      const nextUnread = new Map();
      for (const c of mine) {
        nextUnread.set(c.id, Number(c.unreadStaff) || 0);
      }
      unreadMapRef.current = nextUnread;
      queuedIdsRef.current = nextQueued;
      readyRef.current = true;
    } catch (err) {
      if (
        err?.code === 'UNAUTHORIZED' ||
        err?.code === 'FORBIDDEN' ||
        err?.code === 'TAB_FORBIDDEN' ||
        err?.status === 401 ||
        err?.status === 403
      ) {
        setEnabled(false);
        resetWatchState();
      }
    }
  }, [enabled, isAwayFromDesk, pushToast, resetWatchState]);

  useEffect(() => {
    refreshEnabled();
    const onAuth = () => refreshEnabled();
    window.addEventListener('nexus:auth-changed', onAuth);
    window.addEventListener('storage', onAuth);
    return () => {
      window.removeEventListener('nexus:auth-changed', onAuth);
      window.removeEventListener('storage', onAuth);
    };
  }, [refreshEnabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    poll();
    const id = setInterval(poll, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled, poll]);

  useEffect(
    () => () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
    },
    []
  );

  const openChat = (chatId) => {
    unlockNotifyBeep();
    navigate(`/admin?tab=support_chat&chat=${encodeURIComponent(chatId)}`);
  };

  if (!enabled || toasts.length === 0) return null;

  return (
    <div className="sct-stack" aria-live="polite">
      {toasts.map((t) => {
        const isQueue = t.kind === 'queue';
        return (
          <div
            key={t.id}
            className={`sct-toast${isQueue ? ' sct-toast--queue' : ''}`}
            role="button"
            tabIndex={0}
            onPointerDown={unlockNotifyBeep}
            onClick={() => {
              dismiss(t.id);
              openChat(t.chatId);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                dismiss(t.id);
                openChat(t.chatId);
              }
            }}
          >
            <span className="sct-toast__icon" aria-hidden="true">
              {isQueue ? <FaUserClock /> : <FaHeadset />}
            </span>
            <span className="sct-toast__body">
              <span className="sct-toast__kicker">
                {isQueue
                  ? 'Fila · Aguardando atendimento'
                  : `Chat VIP${t.topicLabel ? ` · ${t.topicLabel}` : ''}`}
              </span>
              <span className="sct-toast__name">{t.name}</span>
              <span className="sct-toast__preview">
                {isQueue && t.topicLabel
                  ? `${t.topicLabel} — ${t.preview}`
                  : t.preview}
              </span>
            </span>
            <button
              type="button"
              className="sct-toast__close"
              aria-label="Fechar"
              onClick={(e) => {
                e.stopPropagation();
                dismiss(t.id);
              }}
            >
              <FaTimes />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export const setAdminTabForNotifier = (tabId) => {
  try {
    sessionStorage.setItem(ADMIN_TAB_KEY, String(tabId || ''));
    window.dispatchEvent(new Event('nexus:admin-tab'));
  } catch {
    /* ignore */
  }
};

export default SupportChatNotifier;
